import * as R from 'ramda';
import {AxiosError, AxiosResponse} from 'axios';
import React, {createContext, FC, useContext} from 'react';

import axios from './axios';
import {useMessage} from '../providers/message/message-provider';
import {useAuth} from '../providers/auth/auth-provider';
import {ApiErrorEntity, ICommonComponentProps} from '../definitions/common.definitions';
import {isAccessTokenExpired} from '../providers/auth/auth-storage';

export type SendRequestT<TProps> = {
  path: string;
  data?: TProps;
  auth?: boolean;
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'UPLOAD';
  headers?: Record<string, string>;
}

export type TransportContextT = {
  send: <TProps = any, TResponse = any>(props: SendRequestT<TProps>) => Promise<TResponse>;
}

const context = createContext<TransportContextT>(null!);
const TransportContextProvider = context.Provider;
const useTransport = () => useContext(context);

const TransportProvider: FC<ICommonComponentProps> = (props) => {
  const {
    children,
  } = props;
  const {
    showError,
  } = useMessage();
  const {
    token,
    clearToken,
    refreshTokens,
  } = useAuth();

  const send = async <TProps = any, TResponse = any>(cfg: SendRequestT<TProps>): Promise<TResponse> => {
    const {
      auth = true,
      method = 'POST',
      data,
      path,
      headers = {},
    } = cfg;
    const executeRequest = async (accessToken?: string) => {
      switch (method) {
        case 'UPLOAD':
          return axios.post(
            path,
            data,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                ...(auth ? { 'Authorization': `Bearer ${accessToken ?? token}` } : {}),
                ...headers,
              },
            });
        case 'POST':
        case 'PATCH':
          return axios.post(
            path,
            JSON.stringify(data),
            {
              headers: {
                'Content-Type': 'application/json',
                ...(auth ? { 'Authorization': `Bearer ${accessToken ?? token}` } : {}),
                ...headers,
              },
            });
        case 'DELETE':
          return axios.delete(
            `${path}/${data}`,
            {
              headers: {
                ...(auth ? { 'Authorization': `Bearer ${accessToken ?? token}` } : {}),
                ...headers,
              },
            }
          );
        case 'GET':
        default:
          return axios.get(
            path,
            {
              headers: {
                ...(auth ? { 'Authorization': `Bearer ${accessToken ?? token}` } : {}),
                ...headers,
              },
              params: data,
            }
          );
      }
    };
    let result: AxiosResponse<TResponse>;
    let accessToken = token;
    if (auth && accessToken && isAccessTokenExpired(accessToken)) {
      try {
        const refreshed = await refreshTokens();
        accessToken = refreshed?.access_token;
      } catch {
        clearToken();
      }
    }
    try {
      result = await executeRequest(accessToken);
    } catch (error: any) {
      if (error instanceof AxiosError && error?.response?.status === 401 && auth) {
        try {
          const refreshed = await refreshTokens();
          if (refreshed?.access_token) {
            result = await executeRequest(refreshed.access_token);
            return result.data;
          }
        } catch {
          clearToken();
        }
      }
      if (error instanceof AxiosError) {
        const errorData = error?.response?.data as ApiErrorEntity;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          clearToken();
        }
        if (R.isNil(errorData)) {
          showError({ message: error.message });
          throw error;
        }
        showError({ message: errorData.message });
        throw errorData;
      }
      showError({ message: error.message });
      throw error;
    }
    return result.data;
  }

  const ctx = {
    send,
  }
  return (
    <TransportContextProvider
      value={ctx}
    >
      {children}
    </TransportContextProvider>
  );
}

export default TransportProvider;

export {
  TransportProvider,
  useTransport,
}
